import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarIcon, Loader2, ArrowLeft, AlertTriangle, X, Upload, FileText, FileSpreadsheet, Download, Trash2 } from 'lucide-react';
import QuoteEstimator from '../components/QuoteEstimator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_CATEGORIES = [
  { id: 'plans_architecte', label: 'Plans Architecte', icon: FileText, accept: '.pdf', color: 'bg-blue-100 text-blue-700' },
  { id: 'cahier_charges', label: 'Cahier des charges', icon: FileText, accept: '.pdf', color: 'bg-purple-100 text-purple-700' },
  { id: 'plans_ingenieur', label: 'Plans Ingénieur', icon: FileText, accept: '.pdf', color: 'bg-teal-100 text-teal-700' },
  { id: 'metre', label: 'Métré (XLSX)', icon: FileSpreadsheet, accept: '.xlsx,.xls', color: 'bg-amber-100 text-amber-700' },
];

const PostJobPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [prefill] = useSearchParams();
  const { user, isCompany } = useAuth();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [pendingFiles, setPendingFiles] = useState({
    plans_architecte: [],
    cahier_charges: [],
    plans_ingenieur: [],
    metre: []
  });
  const [formData, setFormData] = useState({
    title: prefill.get('title') || '',
    description: prefill.get('description') || '',
    trade_categories: [],
    location: prefill.get('location') || '',
    city: prefill.get('city') || '',
    postal_code: prefill.get('postal_code') || '',
    country: prefill.get('country') || 'Belgium',
    latitude: prefill.get('lat') || '',
    longitude: prefill.get('lng') || '',
    start_date: null,
    duration: '',
    budget_min: '',
    budget_max: '',
    is_urgent: false
  });

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const res = await axios.get(`${API}/trades`);
        setTrades(res.data);
      } catch (error) {
        console.error('Error fetching trades:', error);
      }
    };
    fetchTrades();
  }, []);

  useEffect(() => {
    if (!isCompany) {
      toast.error('Seules les entreprises peuvent publier des chantiers');
      navigate('/dashboard');
    }
  }, [isCompany, navigate]);

  const hasVatNumber = !!(user?.vat_number?.trim());

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Auto-geocode when city/postal_code/location changes
  useEffect(() => {
    const geocodeAddress = async () => {
      const city = formData.city?.trim();
      const postal = formData.postal_code?.trim();
      const location = formData.location?.trim();
      const country = formData.country || 'Belgium';
      if (!city || city.length < 2) return;

      const query = location ? `${location}, ${postal} ${city}, ${country}` : postal ? `${postal} ${city}, ${country}` : `${city}, ${country}`;
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`);
        const data = await res.json();
        if (data && data.length > 0) {
          setFormData(prev => ({
            ...prev,
            latitude: data[0].lat,
            longitude: data[0].lon
          }));
        }
      } catch (e) { console.error('Geocoding error:', e); }
    };

    const timeout = setTimeout(geocodeAddress, 800);
    return () => clearTimeout(timeout);
  }, [formData.city, formData.postal_code, formData.location, formData.country]);

  const toggleTrade = (tradeId) => {
    setFormData(prev => ({
      ...prev,
      trade_categories: prev.trade_categories.includes(tradeId)
        ? prev.trade_categories.filter(t => t !== tradeId)
        : [...prev.trade_categories, tradeId]
    }));
  };

  const removeTrade = (tradeId) => {
    setFormData(prev => ({
      ...prev,
      trade_categories: prev.trade_categories.filter(t => t !== tradeId)
    }));
  };

  const getTradeLabel = (tradeId) => {
    const trade = trades.find(t => t.id === tradeId);
    return trade ? (t(`trades.${trade.id}`) || trade.name_fr) : tradeId;
  };

  const handleFileSelect = (category, files) => {
    const fileArray = Array.from(files);
    if (category === 'metre' && (pendingFiles.metre.length + fileArray.length) > 1) {
      toast.error('Un seul fichier métré est autorisé');
      return;
    }
    setPendingFiles(prev => ({
      ...prev,
      [category]: [...prev[category], ...fileArray]
    }));
  };

  const removeFile = (category, index) => {
    setPendingFiles(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }));
  };

  const totalFiles = Object.values(pendingFiles).reduce((sum, arr) => sum + arr.length, 0);

  const uploadDocuments = async (jobId) => {
    let uploaded = 0;
    for (const [category, files] of Object.entries(pendingFiles)) {
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('category', category);
          await axios.post(`${API}/jobs/${jobId}/categorized-docs`, fd, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploaded++;
        } catch (err) {
          console.error(`Upload error for ${file.name}:`, err);
          toast.error(`Erreur upload: ${file.name}`);
        }
      }
    }
    return uploaded;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.trade_categories.length === 0) {
      toast.error('Veuillez sélectionner au moins un corps de métier');
      return;
    }
    setLoading(true);

    try {
      const jobData = {
        ...formData,
        trade_category: formData.trade_categories[0],
        start_date: formData.start_date ? format(formData.start_date, 'yyyy-MM-dd') : '',
        budget_min: parseFloat(formData.budget_min) || 0,
        budget_max: parseFloat(formData.budget_max) || 0,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null
      };

      const res = await axios.post(`${API}/jobs`, jobData, { withCredentials: true });
      const jobId = res.data.id;

      if (totalFiles > 0) {
        setUploadingDocs(true);
        const count = await uploadDocuments(jobId);
        toast.success(`Chantier publié avec ${count} document(s) joint(s)!`);
      } else {
        toast.success('Chantier publié avec succès!');
      }
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la publication');
    } finally {
      setLoading(false);
      setUploadingDocs(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au tableau de bord
        </Button>

        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl text-[#111827]" data-testid="post-job-title">
            {t('jobs.postJob')}
          </h1>
          <p className="text-[#4B5563] mt-1">Publiez un nouveau chantier et trouvez des sous-traitants qualifiés</p>
        </div>

        {!hasVatNumber && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-lg flex items-start gap-3" data-testid="vat-warning">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-red-700">Numéro de TVA ou SIRET obligatoire</p>
              <p className="text-sm text-red-600 mt-1">Vous devez renseigner votre numéro de TVA ou SIRET dans votre profil avant de publier un chantier.</p>
              <Button variant="outline" className="mt-2 border-red-300 text-red-700 hover:bg-red-100" onClick={() => navigate('/dashboard/profile')}>
                Compléter mon profil
              </Button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading">Informations du chantier</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">{t('jobs.jobTitle')} *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Ex: Rénovation toiture immeuble 4 étages"
                  className="input-field"
                  required
                  data-testid="job-title-input"
                />
              </div>

              <div>
                <Label htmlFor="description">{t('jobs.description')} *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Décrivez le chantier en détail: travaux à réaliser, conditions, exigences..."
                  className="min-h-[150px] input-field"
                  required
                  data-testid="job-description-input"
                />
              </div>

              {/* Multi-select trades */}
              <div>
                <Label>Corps de métier(s) * <span className="text-xs text-[#6B7280] font-normal">(sélectionnez un ou plusieurs)</span></Label>
                
                {formData.trade_categories.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2 mb-3" data-testid="selected-trades">
                    {formData.trade_categories.map(tradeId => (
                      <Badge
                        key={tradeId}
                        className="bg-[#1E3A8A] text-white px-3 py-1 gap-1 cursor-pointer hover:bg-[#1E3A8A]/80"
                        onClick={() => removeTrade(tradeId)}
                      >
                        {getTradeLabel(tradeId)}
                        <X className="w-3 h-3" />
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 border rounded-lg bg-white max-h-[260px] overflow-y-auto" data-testid="trades-checkbox-grid">
                  {trades.map((trade) => (
                    <label
                      key={trade.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors text-sm ${
                        formData.trade_categories.includes(trade.id)
                          ? 'bg-[#1E3A8A]/10 text-[#1E3A8A] font-medium'
                          : 'hover:bg-[#F1F5F9] text-[#374151]'
                      }`}
                      data-testid={`trade-option-${trade.id}`}
                    >
                      <Checkbox
                        checked={formData.trade_categories.includes(trade.id)}
                        onCheckedChange={() => toggleTrade(trade.id)}
                      />
                      {t(`trades.${trade.id}`) || trade.name_fr}
                    </label>
                  ))}
                </div>
                {formData.trade_categories.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">Sélectionnez au moins un corps de métier</p>
                )}
              </div>

              <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <div>
                    <p className="font-medium text-red-700">{t('jobs.markUrgent')}</p>
                    <p className="text-sm text-red-600">Ce chantier apparaîtra dans la section urgente</p>
                  </div>
                </div>
                <Switch
                  checked={formData.is_urgent}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_urgent: checked }))}
                  data-testid="job-urgent-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* DOCUMENTS SECTION */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Upload className="w-5 h-5 text-[#1E3A8A]" />
                Documents du chantier
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-[#6B7280]">
                Joignez les plans, cahier des charges et métré pour permettre aux sous-traitants de chiffrer précisément.
              </p>

              {/* Download XLSX template */}
              <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <FileSpreadsheet className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">Template métré XLSX</p>
                  <p className="text-xs text-amber-600">Téléchargez le template, remplissez-le avec vos quantités, puis uploadez-le</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-100"
                  onClick={() => window.open(`${API}/jobs/metre-template`, '_blank')}
                  data-testid="download-metre-template"
                >
                  <Download className="w-4 h-4" />
                  Télécharger
                </Button>
              </div>

              {/* File upload zones per category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {DOC_CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  const files = pendingFiles[cat.id];
                  return (
                    <div key={cat.id} className="border rounded-lg p-3 bg-white" data-testid={`upload-zone-${cat.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-4 h-4 text-[#1E3A8A]" />
                        <span className="text-sm font-medium text-[#374151]">{cat.label}</span>
                      </div>

                      {files.length > 0 && (
                        <div className="space-y-1 mb-2">
                          {files.map((file, idx) => (
                            <div key={`${file.name}-${file.size}`} className="flex items-center gap-2 text-xs bg-[#F9FAFB] p-2 rounded">
                              <Badge className={`${cat.color} text-xs`}>{file.name.split('.').pop().toUpperCase()}</Badge>
                              <span className="flex-1 truncate">{file.name}</span>
                              <span className="text-[#9CA3AF]">{(file.size / 1024).toFixed(0)} KB</span>
                              <button
                                type="button"
                                onClick={() => removeFile(cat.id, idx)}
                                className="text-red-400 hover:text-red-600"
                                data-testid={`remove-file-${cat.id}-${idx}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-[#D1D5DB] rounded-md cursor-pointer hover:border-[#1E3A8A]/50 hover:bg-[#F8FAFC] transition-colors">
                        <Upload className="w-4 h-4 text-[#9CA3AF]" />
                        <span className="text-xs text-[#9CA3AF]">
                          {cat.id === 'metre' ? 'Choisir un fichier XLSX' : 'Choisir un ou plusieurs PDF'}
                        </span>
                        <input
                          type="file"
                          accept={cat.accept}
                          multiple={cat.id !== 'metre'}
                          className="hidden"
                          onChange={(e) => handleFileSelect(cat.id, e.target.files)}
                          data-testid={`file-input-${cat.id}`}
                        />
                      </label>
                    </div>
                  );
                })}
              </div>

              {totalFiles > 0 && (
                <p className="text-sm text-[#1E3A8A] font-medium">
                  {totalFiles} document(s) prêt(s) à être uploadé(s) avec le chantier
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading">Localisation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">{t('jobs.city')} *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Ex: Bruxelles"
                    className="input-field"
                    required
                    data-testid="job-city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="postal_code">Code postal *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-[#1E3A8A] bg-[#1E3A8A]/10 px-1.5 py-0.5 rounded">
                      {formData.country === 'Belgium' ? 'BE' : formData.country === 'France' ? 'FR' : 'LU'}
                    </span>
                    <Input
                      id="postal_code"
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleChange}
                      placeholder={formData.country === 'Belgium' ? '1000' : formData.country === 'France' ? '75000' : '1234'}
                      className="input-field pl-12"
                      required
                      data-testid="job-postal-code-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>{t('jobs.country')} *</Label>
                  <Select 
                    value={formData.country} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
                  >
                    <SelectTrigger data-testid="job-country-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Belgium">Belgique</SelectItem>
                      <SelectItem value="Luxembourg">Luxembourg</SelectItem>
                      <SelectItem value="France">France</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="location">{t('jobs.location')}</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Adresse complète du chantier"
                  className="input-field"
                  data-testid="job-location-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="latitude">Latitude (optionnel)</Label>
                  <Input
                    id="latitude"
                    name="latitude"
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={handleChange}
                    placeholder="ex: 50.8503"
                    className="input-field"
                  />
                </div>
                <div>
                  <Label htmlFor="longitude">Longitude (optionnel)</Label>
                  <Input
                    id="longitude"
                    name="longitude"
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={handleChange}
                    placeholder="ex: 4.3517"
                    className="input-field"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading">Dates & Budget</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{t('jobs.startDate')} *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal input-field"
                        data-testid="job-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.start_date ? (
                          format(formData.start_date, 'PPP', { locale: fr })
                        ) : (
                          <span className="text-[#4B5563]">Sélectionner une date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.start_date}
                        onSelect={(date) => setFormData(prev => ({ ...prev, start_date: date }))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label htmlFor="duration">{t('jobs.duration')} *</Label>
                  <Input
                    id="duration"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    placeholder="Ex: 2 semaines, 3 mois..."
                    className="input-field"
                    required
                    data-testid="job-duration-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="budget_min">Budget minimum (€) <span className="text-xs text-[#6B7280] font-normal">(optionnel)</span></Label>
                  <Input
                    id="budget_min"
                    name="budget_min"
                    type="number"
                    min="0"
                    value={formData.budget_min}
                    onChange={handleChange}
                    placeholder="ex: 5000"
                    className="input-field"
                    data-testid="job-budget-min-input"
                  />
                </div>
                <div>
                  <Label htmlFor="budget_max">Budget maximum (€) <span className="text-xs text-[#6B7280] font-normal">(optionnel)</span></Label>
                  <Input
                    id="budget_max"
                    name="budget_max"
                    type="number"
                    min="0"
                    value={formData.budget_max}
                    onChange={handleChange}
                    placeholder="ex: 10000"
                    className="input-field"
                    data-testid="job-budget-max-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* AI Quote Estimator */}
          <div className="mb-6">
            <QuoteEstimator
              description={formData.description}
              tradeCategory={formData.trade_categories[0] || ''}
              city={formData.city}
              country={formData.country}
            />
          </div>

          <Button 
            type="submit" 
            className="btn-accent w-full text-lg py-6" 
            disabled={loading || uploadingDocs || formData.trade_categories.length === 0}
            data-testid="submit-job"
          >
            {uploadingDocs ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Upload des documents en cours...
              </>
            ) : loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Publication en cours...
              </>
            ) : (
              totalFiles > 0 ? `Publier le chantier (${totalFiles} document${totalFiles > 1 ? 's' : ''})` : 'Publier le chantier'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PostJobPage;
